import React, { useEffect, useMemo, useState } from 'react';
import { ObjectModel, useObjectsTab } from '../items/hooks/HObjects';
import { User } from '../items/interfaces/IUsers';
import ObjectForm from '../dialogs/ObjectForm';
import ObjectDocsForm from '../dialogs/ObjectDocsForm';
import { IconButton, Tooltip, Skeleton, Pagination, Typography } from '@mui/material';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';

const PAGE_SIZE = 8;

const COUNTRY_NAME_TO_ISO: Record<string, string> = {
	italy: 'IT',
	switzerland: 'CH',
	germany: 'DE',
	france: 'FR',
	spain: 'ES',
	portugal: 'PT',
	netherlands: 'NL',
	belgium: 'BE',
	austria: 'AT',
	ireland: 'IE',
	luxembourg: 'LU',
	poland: 'PL',
	greece: 'GR',
	sweden: 'SE',
	norway: 'NO',
	denmark: 'DK',
	finland: 'FI',
	'united kingdom': 'GB',
	uk: 'GB',
	usa: 'US',
	'united states': 'US',
	'united states of america': 'US',
	canada: 'CA',
	australia: 'AU',
	japan: 'JP',
	singapore: 'SG',
	uae: 'AE',
	'united arab emirates': 'AE',
};

const isoToFlag = (isoCode: string) =>
	String.fromCodePoint(
		...isoCode
			.toUpperCase()
			.split('')
			.map((char) => 127397 + char.charCodeAt(0))
	);

const getCountryDisplay = (rawCountry: unknown) => {
	const country = String(rawCountry ?? '').trim();
	if (!country) return { flag: '🏳️', label: '—' };

	const normalized = country.toLowerCase();
	const normalizedCode = normalized === 'uk' ? 'gb' : normalized;
	const isoCode = /^[a-z]{2}$/i.test(normalizedCode)
		? normalizedCode.toUpperCase()
		: COUNTRY_NAME_TO_ISO[normalized] || '';

	if (!isoCode) return { flag: '🏳️', label: country };

	return { flag: isoToFlag(isoCode), label: country };
};

const CURRENCY_SYMBOLS: Record<string, string> = {
	EUR: 'EUR',
	USD: '$',
	GBP: 'GBP',
	CHF: 'CHF',
	AED: 'AED',
	JPY: 'JPY',
};

interface ObjectsDBProps {
	objects?: ObjectModel[];
	selectedRows?: boolean[];
	onToggleSelect?: (index: number) => void;
	onToggleSelectAll?: (select: boolean) => void;
	onEdit?: (object: ObjectModel, updatedData: Partial<ObjectModel>) => Promise<void>;
	onDelete?: (object: ObjectModel) => void;
	user?: User | null;
	selectableFlags?: boolean;
	selectedObjectIds?: Array<string | number>;
	onToggleObjectSelect?: (objectId: string | number) => void;
	onRowClick?: (object: ObjectModel) => void;
}

const cardStyles = {
	grid: {
		display: 'grid',
		gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
		gap: '16px',
	} as React.CSSProperties,
	card: {
		border: '1px solid #eceff1',
		borderRadius: '12px',
		background: 'var(--objectsdb-card-bg, #ffffff)',
		overflow: 'hidden',
		display: 'flex',
		flexDirection: 'column',
		minHeight: '360px',
		boxShadow: '0 2px 8px rgba(15, 23, 42, 0.06)',
	} as React.CSSProperties,
	imageWrap: {
		width: '100%',
		height: '180px',
		background: '#f4f6f8',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	} as React.CSSProperties,
	image: {
		width: '100%',
		height: '100%',
		objectFit: 'cover',
	} as React.CSSProperties,
	content: {
		padding: '12px 14px',
		display: 'flex',
		flexDirection: 'column',
		flex: 1,
	} as React.CSSProperties,
	titleRow: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: '8px',
		marginBottom: '10px',
	} as React.CSSProperties,
	title: {
		fontSize: '15px',
		lineHeight: 1.2,
		fontWeight: 700,
		margin: 0,
	} as React.CSSProperties,
	description: {
		margin: 0,
		color: '#455a64',
		fontSize: '13px',
		lineHeight: 1.45,
	} as React.CSSProperties,
	middle: {
		flex: 1,
	} as React.CSSProperties,
	bottom: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		borderTop: '1px solid #eceff1',
		marginTop: '10px',
		paddingTop: '10px',
		gap: '10px',
		flexWrap: 'wrap',
	} as React.CSSProperties,
	metricLabel: {
		fontSize: '11px',
		color: '#607d8b',
		textTransform: 'uppercase',
		letterSpacing: '0.04em',
		marginBottom: '2px',
	} as React.CSSProperties,
	metricValue: {
		fontSize: '14px',
		fontWeight: 700,
		color: '#1f2937',
	} as React.CSSProperties,
	actions: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-end',
		gap: '2px',
	} as React.CSSProperties,
};

const ObjectsDB: React.FC<ObjectsDBProps> = ({
	objects,
	selectedRows = [],
	onToggleSelect,
	onToggleSelectAll,
	onEdit,
	onDelete,
	user,
	selectableFlags = false,
	selectedObjectIds = [],
	onToggleObjectSelect,
	onRowClick,
}) => {
	const {
		filteredObjects,
		selectedRows: hookSelectedRows,
		toggleRowSelection,
		selectAllRows,
		fetchObjectMainImage,
		createObjectMedia,
	} = useObjectsTab();

	const isAdmin = Number(user?.role_id ?? user?.roleId) === 1;

	const rows = objects ?? filteredObjects;
	const rowSelection = selectedRows.length > 0 ? selectedRows : hookSelectedRows;
	const handleToggle = onToggleSelect ?? toggleRowSelection;
	const handleToggleAll = onToggleSelectAll ?? selectAllRows;
	const [page, setPage] = useState(1);

	// Preserve table-like selection behavior for compatibility with existing parent components.
	useEffect(() => {
		if (!rows.length) {
			if (rowSelection.length) handleToggleAll(false);
			return;
		}

		if (rowSelection.length !== rows.length) {
			handleToggleAll(false);
		}
	}, [rows.length, rowSelection.length, handleToggleAll]);

	useEffect(() => {
		const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
		if (page > totalPages) {
			setPage(totalPages);
		}
	}, [page, rows.length]);

	const pageStartIndex = (page - 1) * PAGE_SIZE;
	const visibleRows = useMemo(
		() => rows.slice(pageStartIndex, pageStartIndex + PAGE_SIZE),
		[rows, pageStartIndex]
	);

	const selectedObjectIdsSet = useMemo(
		() => new Set(selectedObjectIds.map((id) => String(id))),
		[selectedObjectIds]
	);

	// Docs dialog state
	const [docsDialogOpen, setDocsDialogOpen] = useState(false);
	const [selectedObjectId, setSelectedObjectId] = useState<string | number | null>(null);

	// Edit form state
	const [editDialogOpen, setEditDialogOpen] = useState(false);
	const [editingObject, setEditingObject] = useState<ObjectModel | null>(null);

	// Main image state: map of object ID to main image URL
	const [mainImages, setMainImages] = useState<{ [key: string]: string }>({});
	const [mainImagesLoading, setMainImagesLoading] = useState(false);

	useEffect(() => {
		const loadMainImages = async () => {
			setMainImagesLoading(true);
			const imageMap: { [key: string]: string } = {};

			for (const object of visibleRows) {
				const mainImage = await fetchObjectMainImage(object.id);
				if (mainImage?.url) {
					imageMap[String(object.id)] = mainImage.url;
				}
			}

			setMainImages(imageMap);
			setMainImagesLoading(false);
		};

		loadMainImages();
	}, [visibleRows, fetchObjectMainImage]);

	const formatPriceInMillions = (value: string | number | null | undefined) => {
		if (value === null || value === undefined || value === '') return '—';
		const numericValue = Number(value);
		if (Number.isNaN(numericValue)) return String(value);

		const millions = numericValue / 1_000_000;
		const decimals = Number.isInteger(millions) ? 0 : 1;
		return `${millions.toLocaleString('en-US', {
			minimumFractionDigits: decimals,
			maximumFractionDigits: decimals,
		})}M`;
	};

	const formatCurrencyPrice = (currency: unknown, price: unknown) => {
		const code = String(currency ?? '').trim().toUpperCase();
		const symbol = CURRENCY_SYMBOLS[code] || code || '';
		const formattedPrice = formatPriceInMillions(price as string | number | null | undefined);
		if (formattedPrice === '—') return '—';
		return symbol ? `${symbol} ${formattedPrice}` : formattedPrice;
	};

	const getDescriptionPreview = (description: unknown) => {
		const plain = String(description ?? '').replace(/\s+/g, ' ').trim();
		if (!plain) return '—';

		const sentenceSplit = plain.split(/(?<=[.!?])\s+/);
		const firstSentence = sentenceSplit[0] || plain;
		const preview = firstSentence.length > 150 ? `${firstSentence.slice(0, 147)}...` : firstSentence;
		return preview;
	};

	const handleViewDocs = (objectId: string | number) => {
		setSelectedObjectId(objectId);
		setDocsDialogOpen(true);
	};

	const handleCloseDocsDialog = () => {
		setDocsDialogOpen(false);
		setSelectedObjectId(null);
	};

    

	const handleCloseEditDialog = () => {
		setEditDialogOpen(false);
		setEditingObject(null);
	};

	const handleEditSubmit = async (updatedData: Partial<ObjectModel>) => {
		if (!onEdit || !editingObject) return;
		await onEdit(editingObject, updatedData);
		handleCloseEditDialog();
	};

	return (
		<>
			<div style={cardStyles.grid}>
				{visibleRows.map((object, index) => (
					<article
						key={object.id}
						style={{
							...cardStyles.card,
							cursor: onRowClick ? 'pointer' : 'default',
							borderColor: rowSelection[pageStartIndex + index] ? '#90caf9' : '#eceff1',
							boxShadow: rowSelection[pageStartIndex + index]
								? '0 4px 14px rgba(33, 150, 243, 0.25)'
								: cardStyles.card.boxShadow,
						}}
						onClick={() => {
							onRowClick?.(object);
							handleToggle(pageStartIndex + index);
						}}
					>
						<div style={cardStyles.imageWrap}>
							{mainImagesLoading ? (
								<Skeleton variant='rectangular' width='100%' height='100%' />
							) : mainImages[String(object.id)] ? (
								<img src={mainImages[String(object.id)]} alt={object.name} style={cardStyles.image} />
							) : (
								<Skeleton variant='rectangular' width='100%' height='100%' animation={false} />
							)}
						</div>

						<div style={cardStyles.content}>
							<div style={cardStyles.titleRow}>
								<h3 style={cardStyles.title}>{object.name || '—'}</h3>
								{selectableFlags && (
									<span
										role='button'
										aria-label={`Select object ${object.id}`}
										onClick={(event) => {
											event.stopPropagation();
											onToggleObjectSelect?.(object.id);
										}}
										style={{
											cursor: 'pointer',
											fontSize: '18px',
											color: selectedObjectIdsSet.has(String(object.id)) ? '#d32f2f' : '#9e9e9e',
											userSelect: 'none',
										}}
									>
										{selectedObjectIdsSet.has(String(object.id)) ? '🚩' : '⚑'}
									</span>
								)}
							</div>

							<div style={cardStyles.middle}>
								<p style={cardStyles.description}>{getDescriptionPreview(object.description)}</p>
							</div>

							<div style={cardStyles.bottom}>
								<div>
									<div style={cardStyles.metricLabel}>Price</div>
									<div style={cardStyles.metricValue}>{formatCurrencyPrice(object.currency, object.price)}</div>
								</div>
								<div>
									<div style={cardStyles.metricLabel}>Yield</div>
									<div style={cardStyles.metricValue}>
										{object.yield_net ? `${Number(object.yield_net).toFixed(2)}%` : '—'}
									</div>
								</div>
								<div
									style={cardStyles.actions}
									onClick={(event) => {
										event.stopPropagation();
									}}
								>
		
									<Tooltip title='Documents'>
										<IconButton
											size='small'
											color='primary'
											onClick={() => {
												handleViewDocs(object.id);
											}}
										>
											<FolderOpenIcon fontSize='small' />
										</IconButton>
									</Tooltip>
									<span
										aria-label={`Country ${getCountryDisplay(object.country).label}`}
										title={getCountryDisplay(object.country).label}
										style={{ fontSize: '18px', lineHeight: 1, marginLeft: '6px' }}
									>
										{getCountryDisplay(object.country).flag}
									</span>
								</div>
							</div>
						</div>
					</article>
				))}
			</div>

			<div className='deals-pagination-row'>
				<Typography variant='body2' color='text.secondary'>
					{rows.length} object{rows.length !== 1 ? 's' : ''}
				</Typography>
				{rows.length > PAGE_SIZE && (
					<Pagination
						count={Math.ceil(rows.length / PAGE_SIZE)}
						page={page}
						onChange={(_, value) => setPage(value)}
						size='small'
						color='primary'
						shape='rounded'
					/>
				)}
			</div>

			<ObjectDocsForm
				open={docsDialogOpen}
				onClose={handleCloseDocsDialog}
				objectId={selectedObjectId}
				canUpload={isAdmin}
				onFileDrop={async (file, objectId) => {
					await createObjectMedia(objectId, file);
				}}
			/>

			{isAdmin && onEdit && (
				<ObjectForm
					open={editDialogOpen}
					onClose={handleCloseEditDialog}
					onSubmit={handleEditSubmit}
					defaultValues={editingObject || undefined}
				/>
			)}
		</>
	);
};

export default ObjectsDB;
